<template>
  <BaseModal class="product-modal" v-model="isOpenModal">
    <template v-slot:header>
      {{ header }}
    </template>
    <BaseOrderModal
      @closeOrder="openedOrder = ''"
      :orderUUID="openedOrder"
      v-model="showOrderModal"
      @orderUpdated="reloadQuotes"
      @quoteConverted="openQuote"
    />
    <BaseProductModal
      v-model="showProductModal"
      @closeProduct="openedProduct = ''"
      :code="openedProduct"
    />
    <div class="product-view" v-if="!loading">
      <!--  Navigation  -->
      <div class="navigation" v-if="customerFull">
        <div class="bar">
          <button
            @click="selectedSection = 'general'"
            class="tablinks"
            :class="{ active: selectedSection == 'general' }"
          >
            {{ $t("message.base_customer_modal.tabs.general") }}
          </button>
          <button
            @click="selectedSection = 'address'"
            class="tablinks"
            :class="{ active: selectedSection == 'address' }"
          >
            {{ $t("message.base_customer_modal.tabs.address") }}
          </button>
          <button
            @click="selectedSection = 'orders'"
            class="tablinks"
            :class="{ active: selectedSection == 'orders' }"
          >
            {{ $t("message.base_customer_modal.tabs.orders_placed") }}
          </button>
          <button
            @click="selectedSection = 'quotes'"
            class="tablinks"
            :class="{ active: selectedSection == 'quotes' }"
          >
            {{ $t("message.quotes_page.quotes_tab") }}
          </button>
          <button
            @click="selectedSection = 'products_ordered'"
            class="tablinks"
            :class="{ active: selectedSection == 'products_ordered' }"
          >
            {{ $t("message.base_customer_modal.tabs.products_ordered") }}
          </button>
          <button
            @click="selectedSection = 'shopping-cart-items'"
            class="tablinks"
            :class="{ active: selectedSection == 'shopping-cart-items' }"
          >
            {{ $t("message.base_customer_modal.tabs.shopping_cart_items") }}
          </button>
          <button
            @click="selectedSection = 'escala'"
            class="tablinks"
            :class="{ active: selectedSection == 'escala' }"
          >
            {{ $t("message.base_customer_modal.tabs.escala") }}
          </button>
          <!-- The infoauto customer is the account this user buys for: its
               data, documents and orders live in its own modal -->
          <button
            v-if="customerFull.generalInfo.infoautoId"
            @click="openInfoautoAccount"
            class="tablinks account-link"
            :title="customerFull.generalInfo.infoautoId"
          >
            <i class="mdi mdi-domain"></i>
            {{ $t("message.base_customer_modal.infoauto_account") }}:
            {{
              infoautoCustomer
                ? infoautoCustomer.generalInfo.name
                : customerFull.generalInfo.infoautoId
            }}
            <i class="mdi mdi-open-in-app"></i>
          </button>
        </div>
      </div>
      <div class="content" v-if="customerFull">
        <!-- main section - box -->
        <template v-if="selectedSection == 'general'">
          <!-- general info -->
          <div class="box full">
            <div class="box-header">
              {{ $t("message.base_customer_modal.main_info") }}
            </div>
            <div
              v-if="alerts.length > 0"
              class="box-content-block bg-red-400 p-2 text-center text-white"
            >
              <div v-for="alert in alerts" :key="alert.message">
                {{ $t(`message.base_customer_modal.alerts.${alert.type}`) }}
              </div>
            </div>
            <div
              v-if="customerFull.generalInfo.status == 0"
              class="bg-red-400 p-2 text-center text-white"
            >
              {{ $t("message.base_customer_modal.user_disabled") }}
            </div>
            <div class="box-content-block">
              {{ customerFull.generalInfo.name }}
              {{ customerFull.generalInfo.surname }}
              <br />
              <!-- Three-column responsive grid -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                <!-- Column 1: Personal Info -->
                <div>
                  <div class="input-item">
                    <label for="name">{{
                      $t("message.base_customer_modal.name")
                    }}</label>
                    <input
                      id="name"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.name"
                      :disabled="!editCustomer"
                    />
                  </div>
                  <div class="input-item">
                    <label for="surname">{{
                      $t("message.base_customer_modal.surname")
                    }}</label>
                    <input
                      id="surname"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.surname"
                      :disabled="!editCustomer"
                    />
                  </div>
                  <div class="input-item">
                    <label for="company">{{
                      $t("message.base_customer_modal.company")
                    }}</label>
                    <input
                      id="company"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.companyName"
                      :disabled="!editCustomer"
                    />
                  </div>
                  <div class="input-item">
                    <label for="created_at">{{
                      $t("message.base_customer_modal.created_at")
                    }}</label>
                    <input
                      id="created_at"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.createdAt"
                      :disabled="true"
                    />
                  </div>
                  <div class="input-item">
                    <label for="email">{{
                      $t("message.base_customer_modal.email")
                    }}</label>
                    <input
                      id="email"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.email"
                      :disabled="true"
                    />
                  </div>
                  <div class="input-item">
                    <label for="phone">{{
                      $t("message.base_customer_modal.phone")
                    }}</label>
                    <div class="flex flex-row gap-2">
                      <input
                        id="phone-area-code"
                        type="text"
                        class="regular clear w-24"
                        :placeholder="
                          $t('create_infoauto_customer_modal.area_code')
                        "
                        :maxlength="areaCodeMaxLength"
                        v-model.trim="customerFull.generalInfo.phoneAreaCode"
                        :disabled="!editCustomer"
                      />
                      <input
                        id="phone"
                        type="text"
                        class="regular clear flex-1"
                        v-model="customerFull.generalInfo.phone"
                        :disabled="!editCustomer"
                      />
                    </div>
                  </div>
                  <div class="input-item">
                    <label for="infoauto-id">{{
                      $t("message.base_customer_modal.infoauto_id")
                    }}</label>
                    <div class="flex flex-row items-center gap-2">
                      <input
                        id="infoauto-id"
                        type="text"
                        class="regular clear flex-1"
                        v-model="customerFull.generalInfo.infoautoId"
                        :disabled="!editCustomer"
                      />
                      <button
                        v-if="editCustomer"
                        @click="showInfoautoCustomerFinder = true"
                        class="btn-dark-button whitespace-nowrap"
                        title="Search Infoauto Customer"
                      >
                        <i class="mdi mdi-magnify"></i> Select
                      </button>
                    </div>
                    <div
                      v-if="loadingInfoauto"
                      class="text-gray-500 text-sm mt-1"
                    >
                      Validating...
                    </div>
                    <div
                      v-else-if="infoautoCustomer"
                      class="bg-green-100 border border-green-400 rounded-md p-2 mt-1 text-sm"
                    >
                      <i class="mdi mdi-check-circle text-green-600"></i>
                      <span class="text-green-700 font-semibold">
                        {{ infoautoCustomer.generalInfo.name }}
                        {{ infoautoCustomer.generalInfo.surname }}
                      </span>
                      <span
                        v-if="infoautoCustomer.generalInfo.companyName"
                        class="text-green-600 text-xs ml-1"
                      >
                        ({{ infoautoCustomer.generalInfo.companyName }})
                      </span>
                    </div>
                    <div
                      v-else-if="
                        infoautoError && customerFull.generalInfo.infoautoId
                      "
                      class="bg-red-100 border border-red-400 rounded-md p-2 mt-1 text-sm"
                    >
                      <i class="mdi mdi-alert-circle text-red-600"></i>
                      <span class="text-red-700">
                        Infoauto ID not found or invalid
                      </span>
                    </div>
                    <div
                      v-else-if="
                        !customerFull.generalInfo.infoautoId ||
                          customerFull.generalInfo.infoautoId === ''
                      "
                      class="bg-yellow-100 border border-yellow-400 rounded-md p-2 mt-1 text-sm"
                    >
                      <i class="mdi mdi-alert text-yellow-600"></i>
                      <span class="text-yellow-700">
                        No Infoauto ID assigned
                      </span>
                    </div>
                    <InfoautoCustomerFinder
                      v-if="showInfoautoCustomerFinder"
                      @close="showInfoautoCustomerFinder = false"
                      @selectedCustomer="selectedInfoautoCustomer"
                    ></InfoautoCustomerFinder>
                  </div>
                </div>
                <!-- Column 2: Account Settings -->
                <div>
                  <div class="input-item">
                    <label for="seller_email">{{
                      $t("message.base_customer_modal.seller")
                    }}</label>
                    <select
                      id="seller_email"
                      name="seller_email"
                      class="regular full bordered"
                      v-model="customerFull.generalInfo.sellerEmail"
                      :disabled="
                        !editCustomer || currentUser.role !== 'superadmin'
                      "
                    >
                      <option
                        v-for="seller in sellers"
                        :key="seller.id"
                        :value="seller.email"
                        >{{ seller.name }}
                      </option>
                    </select>
                  </div>
                  <div class="input-item">
                    <label for="website">{{
                      $t("message.base_customer_modal.created_on_website")
                    }}</label>
                    <input
                      id="website"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.website"
                      :disabled="true"
                    />
                  </div>
                  <div class="input-item">
                    <label for="price_level">{{
                      $t("message.base_customer_modal.price_level")
                    }}</label>
                    <select
                      id="price_level"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.priceLevel"
                      :disabled="
                        !editCustomer || currentUser.role !== 'superadmin'
                      "
                    >
                      <option
                        v-for="item in priceLevelOptions"
                        :key="item.value"
                        :value="item.value"
                        >{{ item.label }}</option
                      >
                    </select>
                  </div>
                  <div class="input-item">
                    <label for="price_level_requested">{{
                      $t("message.base_customer_modal.price_level_requested")
                    }}</label>
                    <select
                      id="price_level_requested"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.priceLevelRequested"
                      disabled
                    >
                      <option
                        v-for="item in priceLevelOptions"
                        :key="item.value"
                        :value="item.value"
                        >{{ item.label }}</option
                      >
                    </select>
                  </div>
                  <div class="input-item">
                    <label for="credit">{{
                      $t("message.base_customer_modal.credit")
                    }}</label>
                    <select
                      id="credit"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.credit"
                      :disabled="!editCustomer"
                    >
                      <option value="0">{{
                        $t("message.base_customer_modal.no")
                      }}</option>
                      <option value="1">{{
                        $t("message.base_customer_modal.yes")
                      }}</option>
                    </select>
                  </div>
                  <div class="input-item">
                    <label for="tax">{{
                      $t("message.base_customer_modal.taxable")
                    }}</label>
                    <select
                      id="tax"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.tax"
                      :disabled="!editCustomer"
                    >
                      <option value="0">{{
                        $t("message.base_customer_modal.no")
                      }}</option>
                      <option value="1">{{
                        $t("message.base_customer_modal.yes")
                      }}</option>
                    </select>
                  </div>
                </div>
                <!-- Column 3: Status & Actions -->
                <div
                  class="self-start sticky top-0 bg-white rounded-md border border-gray-200 p-3 shadow-sm"
                >
                  <div class="input-item">
                    <label>{{
                      $t("message.base_customer_modal.user_status")
                    }}</label>
                    <span class="font-semibold">{{ userStatus }}</span>
                    <template v-if="editCustomer">
                      <BaseSwitchCustom
                        v-model="customerFull.generalInfo.status"
                      />
                    </template>
                  </div>
                  <div class="flex flex-col gap-2 mt-3">
                    <button
                      v-if="editCustomer && customerUpdated"
                      @click="saveMainInfo()"
                      class="btn-success-button"
                    >
                      {{ $t("message.base_customer_modal.save_updates") }}
                    </button>
                    <button
                      v-if="editCustomer"
                      @click="revertMainInfo()"
                      class="btn-dark-button"
                    >
                      {{ $t("message.base_customer_modal.cancel_update") }}
                    </button>
                    <button
                      v-if="!editCustomer"
                      @click="editCustomer = true"
                      class="btn-dark-button"
                    >
                      {{ $t("message.base_customer_modal.edit_customer") }}
                    </button>
                  </div>
                  <hr class="my-3 border-gray-200" />
                  <div class="input-item">
                    <label for="show_prices">{{
                      $t("message.base_customer_modal.show_stock")
                    }}</label>
                    <select
                      id="show_prices"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.showStock"
                      :disabled="!editCustomer"
                    >
                      <option value="0">{{
                        $t("message.base_customer_modal.no")
                      }}</option>
                      <option value="1">{{
                        $t("message.base_customer_modal.yes")
                      }}</option>
                    </select>
                  </div>
                  <div class="input-item">
                    <label for="is_seller" class="mb-2"
                      >{{ $t("message.base_customer_modal.is_seller") }}
                      <span
                        v-if="
                          customerFull.generalInfo.isAdmin > 0 &&
                            !customerFull.sellerData
                        "
                        class="bg-red-cancel px-2 py-1 rounded-md text-white"
                      >
                        {{ $t("message.base_customer_modal.no_verificado") }}
                      </span>
                      <span
                        v-else-if="customerFull.generalInfo.isAdmin > 0"
                        class="bg-green-success px-2 py-1 rounded-md text-white"
                      >
                        {{
                          $t(
                            "message.base_customer_modal.verificado_en_infoauto_como"
                          )
                        }}
                        <b>{{ customerFull.sellerData.nombreVendedor }}</b>
                      </span>
                    </label>
                    <select
                      id="is_seller"
                      type="text"
                      class="regular clear"
                      v-model="customerFull.generalInfo.isAdmin"
                      :disabled="
                        !editCustomer || currentUser.role !== 'superadmin'
                      "
                    >
                      <option
                        v-for="level in [0, 1, 2, 3]"
                        :key="level"
                        :value="String(level)"
                        >{{ level }} -
                        {{
                          $t(
                            `message.base_customer_modal.admin_levels.${level}`
                          )
                        }}</option
                      >
                    </select>
                  </div>
                  <hr class="my-3 border-gray-200" />
                  <!-- Resale Tax -->
                  <div
                    :class="{
                      'bg-green-100': customerTaxable,
                      'bg-red-100': !customerTaxable
                    }"
                    class="border rounded-md border-solid border-gray-400 overflow-hidden p-3 text-center text-sm"
                  >
                    <div v-if="customerTaxable" class="font-semibold">
                      {{ $t("message.base_customer_modal.customer_taxable") }}
                    </div>
                    <div v-else class="font-semibold">
                      {{
                        $t("message.base_customer_modal.customer_not_taxable")
                      }}
                    </div>
                    <div class="mt-1">
                      <span
                        v-if="customerFull.validResaleTax.length > 0"
                        class="text-green-600"
                      >
                        {{ $t("message.base_customer_modal.valid_resale_tax") }}
                      </span>
                      <span v-else class="text-red-600">
                        {{
                          $t("message.base_customer_modal.no_valid_resale_tax")
                        }}
                      </span>
                    </div>
                    <button
                      class="btn-dark-button mt-2 w-full"
                      @click="showManageResaleTax = true"
                    >
                      {{ $t("message.base_customer_modal.manage_resale_tax") }}
                    </button>
                  </div>
                </div>
              </div>
              <ManageResaleTaxModal
                v-if="showManageResaleTax"
                :customerEmail="customerFull.generalInfo.email"
                :customerName="customerFull.generalInfo.name"
                :customerInfoautoId="customerFull.generalInfo.infoautoId"
                @close="showManageResaleTax = false"
                @resaleTaxCreated="resaleTaxCreated"
              ></ManageResaleTaxModal>
            </div>
          </div>
        </template>
        <!-- Customer Address -->
        <template v-if="selectedSection == 'address'">
          <!-- Address -->
          <div class="box full">
            <div class="box-header">
              {{ $t("message.base_customer_modal.customer_address") }}
            </div>
            <div
              class="box-content-block grid grid-cols-1 md:grid-cols-4 gap-3"
            >
              <div
                class="address-box border border-light-700 rounded-md p-4
                 grid gap-1"
                v-for="address in customerFull.address"
                :key="address.id"
              >
                <div class="rounded-full bg-gray-400 text-white w-min p-2">
                  {{ address.id }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.name") }}:
                  {{ address.name }}
                </div>
                <div>
                  {{
                    $t("message.base_customer_modal.address_fields.company")
                  }}: {{ address.company }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.street") }}:
                  {{ address.street1 }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.street") }}:
                  {{ address.street2 }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.street") }}:
                  {{ address.street3 }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.city") }}:
                  {{ address.city }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.state") }}:
                  {{ address.state }}
                </div>
                <div>
                  {{
                    $t(
                      "message.base_customer_modal.address_fields.postal_code"
                    )
                  }}: {{ address.postal_code }}
                </div>
                <div>
                  {{
                    $t("message.base_customer_modal.address_fields.country")
                  }}: {{ address.country }}
                </div>
                <div>
                  {{ $t("message.base_customer_modal.address_fields.phone") }}:
                  {{ address.phone }}
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- related products -->
        <template v-if="selectedSection == 'orders'">
          <!-- products related -->
          <div class="box full">
            <div class="box-header">
              {{ $t("message.base_customer_modal.orders_placed") }}
            </div>
            <div class="box-content-block">
              <table class="default-table">
                <thead>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.orders_table.order_number"
                      )
                    }}
                  </th>
                  <th>
                    {{ $t("message.base_customer_modal.orders_table.date") }}
                  </th>
                  <th>
                    {{
                      $t("message.base_customer_modal.orders_table.products")
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.orders_table.products_quantity"
                      )
                    }}
                  </th>
                  <th>
                    {{ $t("message.base_customer_modal.orders_table.total") }}
                  </th>
                  <th>
                    {{
                      $t("message.base_customer_modal.orders_table.created_by")
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.orders_table.payment_status"
                      )
                    }}
                  </th>
                </thead>
                <tbody>
                  <tr
                    v-for="item in customerFull.orders"
                    :key="item.orderNumber"
                  >
                    <td @click="openOrder(item)" class="center-text">
                      {{ item.orderNumber }}
                    </td>
                    <td class="center-text">{{ item.createDate }}</td>
                    <td class="center-text">{{ item.productsLines }}</td>
                    <td class="center-text">{{ item.productsQuantity }}</td>
                    <td class="center-text">{{ item.total }}</td>
                    <td class="center-text">{{ item.createdBy }}</td>
                    <template v-if="item.statusPayment == 1">
                      <td class="center-text text-green-success">
                        {{
                          $t("message.base_customer_modal.orders_table.paid")
                        }}
                      </td>
                    </template>
                    <template v-else>
                      <td class="center-text text-red-cancel">
                        {{
                          $t("message.base_customer_modal.orders_table.pending")
                        }}
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
        <!-- ordered products -->
        <template v-if="selectedSection == 'products_ordered'">
          <!-- products ordered -->
          <div class="box full">
            <div class="box-header">
              {{ $t("message.base_customer_modal.products_ordered") }}
            </div>
            <div class="box-content-block">
              <table class="default-table">
                <thead>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.products_ordered_table.nkr_code"
                      )
                    }}
                  </th>
                  <th class="hidden md:block">
                    {{
                      $t(
                        "message.base_customer_modal.products_ordered_table.description"
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.products_ordered_table.quantity_ordered"
                      )
                    }}
                  </th>
                  <th>
                    {{
                      $t(
                        "message.base_customer_modal.products_ordered_table.orders"
                      )
                    }}
                  </th>
                </thead>
                <tbody>
                  <tr
                    v-for="item in customerFull.itemsOrdered"
                    :key="item.nkrCode"
                  >
                    <td @click="openProduct(item.nkrCode)" class="center-text">
                      {{ item.nkrCode }}
                    </td>
                    <td class="left-text text-sm hidden md:block">
                      {{ item.description }}
                    </td>
                    <td class="center-text">{{ item.quantityOrdered }}</td>
                    <td class="center-text">{{ item.orders }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
        <template v-if="selectedSection == 'shopping-cart-items'">
          <ShoppingCartItems
            :priceLevel="customerFull.generalInfo.priceLevel"
            :customerId="customerFull.generalInfo.id"
            @viewProduct="openProduct"
          />
        </template>
        <!-- Quotes of this web customer: tied by email, like its orders -->
        <template v-if="selectedSection == 'quotes'">
          <QuotesList
            ref="quotesList"
            :customerEmail="customerFull.generalInfo.email"
            class="w-full"
            @openQuote="openQuote"
          />
        </template>
        <template v-if="selectedSection == 'escala'">
          <EscalaCustomer :customerId="customerId"></EscalaCustomer>
        </template>
      </div>
    </div>
    <div v-else class="loading-full">
      <BaseSmallSpinner />
    </div>
  </BaseModal>
</template>
<script>
import {
  getCustomer,
  saveCustomerGeneralInfo,
  getPriceLevels
} from "@/api/store/CustomersService.js";
import { infoautoGetSingleCustomer } from "@/api/store/Infoauto.js";
import ShoppingCartItems from "@/components/customer/ShoppingCartItems.vue";
import InfoautoCustomerFinder from "../customer/InfoautoCustomerFinder.vue";
import EscalaCustomer from "../customer/EscalaCustomer.vue";
import ManageResaleTaxModal from "@/components/customer/ManageResaleTaxModal";
import QuotesList from "@/components/quotes/QuotesList.vue";
import {
  AREA_CODE_MAX_LENGTH,
  defaultAreaCode
} from "@/constants/infoautoPersona";

export default {
  components: {
    ManageResaleTaxModal,
    ShoppingCartItems,
    InfoautoCustomerFinder,
    EscalaCustomer,
    QuotesList
  },
  props: {
    customerId: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showManageResaleTax: false,
      showInfoautoCustomerFinder: false,
      customerFull: null,
      processing: false,
      isOpenModal: this.value,
      editCustomer: false,
      areaCodeMaxLength: AREA_CODE_MAX_LENGTH,
      customerMainInfoOriginal: null,
      selectedSection: "general",
      loading: true,
      showOrderModal: false,
      openedOrder: "",
      openedProduct: "",
      showProductModal: false,
      priceLevelOptions: [],
      infoautoCustomer: null,
      loadingInfoauto: false,
      infoautoError: false
    };
  },
  mounted() {
    this.priceLevelOptions = getPriceLevels().filter(priceLevel => {
      if (priceLevel.permission) {
        return this.$store.getters["auth/userHasPermission"](
          priceLevel.permission
        );
      }
      return true;
    });
  },
  computed: {
    customerTaxable() {
      if (!this.customerFull) return false;
      if (this.customerFull.validResaleTax.length > 0) {
        return false;
      }
      if (this.customerFull.generalInfo.tax == "0") {
        return false;
      }

      return true;
    },
    currentUserPermissions() {
      return this.$store.getters["auth/currentUserPermissions"];
    },
    userStatus() {
      const ACCESS_VERIFICATION_PENDING = -2;
      const EMAIL_VERIFICATION_PENDING = -1;
      const DISABLED = 0;
      const ENABLED = 1;

      if (!this.customerFull || !this.customerFull.generalInfo) {
        return this.$t(
          "message.base_customer_modal.user_status_values.data_not_loaded"
        );
      }

      console.log(this.customerFull.generalInfo.status);

      const status = Number(this.customerFull.generalInfo.status);

      switch (status) {
        case ACCESS_VERIFICATION_PENDING:
          return this.$t(
            "message.base_customer_modal.user_status_values.email_not_verified"
          );
        case EMAIL_VERIFICATION_PENDING:
          return this.$t(
            "message.base_customer_modal.user_status_values.email_not_verified"
          );
        case DISABLED:
          return this.$t(
            "message.base_customer_modal.user_status_values.disabled"
          );
        case ENABLED:
          return this.$t(
            "message.base_customer_modal.user_status_values.enabled"
          );
        default:
          return this.$t(
            "message.base_customer_modal.user_status_values.unknown"
          );
      }
    },
    alerts() {
      if (!this.customerFull) return [];

      const alertsList = [];

      if (
        !this.customerFull.generalInfo.infoautoId ||
        this.customerFull.generalInfo.infoautoId == ""
      ) {
        alertsList.push({
          type: "no_infoauto_id",
          message: "This customer does not have an infoauto id"
        });
      }

      return alertsList;
    },
    sellers() {
      return this.$store.getters["general/getSellers"];
    },
    currentUser() {
      return this.$store.getters["auth/currentUser"];
    },
    header() {
      if (!this.customerFull) return "";
      return `Customer - ${this.customerFull.generalInfo.name} ${this.customerFull.generalInfo.surname}`;
    },
    isOpen() {
      return this.value;
    },
    customerUpdated() {
      if (!this.customerMainInfoOriginal) {
        return false;
      }
      return (
        this.customerMainInfoOriginal !=
        JSON.stringify(this.customerFull.generalInfo)
      );
    }
  },
  watch: {
    // Sin teléfono todavía: arranca con el prefijo del país. Si ya hay número
    // sin código se deja vacío, suele traer el prefijo adentro.
    editCustomer(editing) {
      const info = this.customerFull?.generalInfo;
      if (editing && info && !info.phone && !info.phoneAreaCode) {
        this.$set(
          info,
          "phoneAreaCode",
          defaultAreaCode(this.$store.getters["general/getActiveStore"]?.code)
        );
      }
    },
    isOpenModal(val) {
      this.$emit("input", val);
      if (!val) {
        this.$emit("closedCustomer");
      }
    },
    value(val) {
      this.isOpenModal = val;
    },
    customerId(val) {
      if (val != "") {
        this.selectedSection = "general";
        this.customerFull = null;
        this.getCustomer();
      }
    }
  },
  methods: {
    resaleTaxCreated() {
      this.showManageResaleTax = false;
      this.getCustomer();
    },
    selectedInfoautoCustomer(customer) {
      this.customerFull.generalInfo.infoautoId = customer.personaId;
      this.showInfoautoCustomerFinder = false;
      this.loadInfoautoCustomer();
    },
    async getCustomer() {
      this.customerFull = await getCustomer(this.customerId);
      this.customerMainInfoOriginal = JSON.stringify(
        this.customerFull.generalInfo
      );
      this.loading = false;
      this.loadInfoautoCustomer();
    },
    async loadInfoautoCustomer() {
      const infoautoId = this.customerFull?.generalInfo?.infoautoId;
      if (!infoautoId) {
        this.infoautoCustomer = null;
        this.infoautoError = false;
        return;
      }
      this.loadingInfoauto = true;
      this.infoautoError = false;
      this.infoautoCustomer = null;
      try {
        this.infoautoCustomer = await infoautoGetSingleCustomer(infoautoId);
      } catch {
        this.infoautoError = true;
      } finally {
        this.loadingInfoauto = false;
      }
    },
    openInfoautoAccount() {
      const infoautoId = this.customerFull.generalInfo.infoautoId;
      // The customers page opens it in place; anywhere else, go to that page
      if (this.$listeners.openInfoauto) {
        this.$emit("openInfoauto", infoautoId);
        return;
      }
      this.isOpenModal = false;
      this.$router
        .push({
          name: "InfoautoCustomersPage",
          query: { ia_open: infoautoId }
        })
        .catch(() => {});
    },
    revertMainInfo() {
      this.customerFull.generalInfo = JSON.parse(this.customerMainInfoOriginal);
      this.editCustomer = false;
    },
    openOrder(order) {
      this.openedOrder = order.orderNumber;
      this.showProductModal = false;
      this.openedProduct = "";
      this.showOrderModal = true;
    },
    openQuote(orderNumber) {
      this.openOrder({ orderNumber });
    },
    reloadQuotes() {
      if (this.$refs.quotesList) this.$refs.quotesList.reload();
    },
    openProduct(nkrCode) {
      this.showOrderModal = false;
      this.openedOrder = "";
      this.openedProduct = nkrCode;
      this.showProductModal = true;
    },
    saveMainInfo() {
      this.editCustomer = false;
      saveCustomerGeneralInfo(this.customerFull.generalInfo)
        .then(res => {
          this.customerFull = res;
          this.customerMainInfoOriginal = JSON.stringify(
            this.customerFull.generalInfo
          );
          this.$toast.open({
            message: this.$t(
              "message.base_customer_modal.toast.user_updated_success"
            ),
            type: "success",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: this.$t(
              "message.base_customer_modal.toast.error_updating_customer"
            ),
            type: "error",
            position: "top-right"
          });
        });
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";
.tablinks.account-link {
  margin-left: auto;
  color: #1d4ed8;
  font-weight: 600;
  white-space: nowrap;
}
.loading-full {
  height: 100%;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.product-modal {
  &:focus {
    outline: none;
  }
  &.modal {
    padding-top: 70px;
    .modal-content {
      height: 98%;
      width: 80%;
      padding: 0px;
      overflow: hidden;
      @media screen and (max-width: 1600px) {
        width: 98%;
      }
    }
    .modal-bg {
      height: calc(100% - 40px);
    }
  }
}
.product-view {
  height: 100%;
  width: 100%;
  overflow: hidden;
  padding-bottom: 20px;
  .content {
    padding: 10px 10px;
    width: 100%;
    height: calc(100% - 80px) !important;
    flex-direction: row;
    overflow-y: auto;
    position: relative;
    flex-grow: 1;
    align-content: flex-start;
    display: inline-flex;
    flex-wrap: wrap;
    justify-content: space-around;
    .box {
      width: 33%;
      min-width: 300px;
      border: 1px solid grey;
      border-radius: 5px;
      margin-bottom: 5px;
      &.full {
        width: 100%;
      }
      .box-header {
        width: 100%;
        padding: 5px 10px;
        background-color: grey;
        color: white;
      }
      table {
        width: 100%;
      }
      .box-content-block {
        width: 100%;
        padding: 5px 10px;
        .input-item {
          margin: 5px 0px;
          display: flex;
          flex-direction: column;
          .badge-list {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            margin: 10px 0px;
          }
        }
      }
    }
  }
}
</style>
